// Enter / Escape press the open popup's primary / cancel button (App.jsx's popup key handler).
// With more than one popup open — the feedback form opened over a dialog by its shortcut —
// the key belongs to the one on top, not to whichever comes first in the document.

/** The CSS z-index of a positioned element, or NaN for a static or auto one. */
function cssZIndex(el) {
  const style = getComputedStyle(el);
  return style.position === "static" ? NaN : Number.parseInt(style.zIndex, 10);
}

/** Whether `a` comes after `b` in the document. */
function laterInDocument(a, b) {
  return (b.compareDocumentPosition(a) & 4) !== 0; // Node.DOCUMENT_POSITION_FOLLOWING
}

/**
 * The stacking contexts `el` paints in, outermost first: its positioned ancestors (itself
 * included) that set a z-index, each as `{ node, z }`.
 */
export function stackChain(el, zIndexOf = cssZIndex) {
  const chain = [];
  for (let node = el; node && node.nodeType === 1; node = node.parentElement) {
    const z = zIndexOf(node);
    if (Number.isFinite(z)) chain.unshift({ node, z });
  }
  return chain;
}

/**
 * Positive when `a` paints above `b`. Below the stacking contexts they share, each side's
 * own context (or the element itself, at level 0, when it has none there) is compared:
 * the higher z-index wins, and on equal ones the later in the document, since a context
 * paints as a whole and nothing inside it can lift it over a sibling context.
 */
export function comparePaintOrder(a, b, { zIndexOf = cssZIndex, later = laterInDocument } = {}) {
  const ca = stackChain(a, zIndexOf);
  const cb = stackChain(b, zIndexOf);
  let i = 0;
  while (i < ca.length && i < cb.length && ca[i].node === cb[i].node) i += 1;
  const za = ca[i]?.z ?? 0;
  const zb = cb[i]?.z ?? 0;
  if (za !== zb) return za - zb;
  const ra = ca[i]?.node ?? a;
  const rb = cb[i]?.node ?? b;
  if (ra === rb) return 0;
  return later(ra, rb) ? 1 : -1;
}

/** The topmost element matching `selector`, or null. */
export function topmostMatch(doc, selector, opts) {
  let best = null;
  for (const el of doc.querySelectorAll(selector)) {
    if (best === null || comparePaintOrder(el, best, opts) > 0) best = el;
  }
  return best;
}
